import React from 'react';
import {
  Apple,
  ArrowForward,
  CheckCircle,
  Devices,
  PeopleAlt,
  } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';
import { translatePublicSite } from '../i18n/publicSiteText';

type ModulePage = {
  path: string;
  name: string;
  keyword: string;
  summary: string;
  about: string;
  capabilities: string[];
  audiences: string[];
};

export const iosModuleLandingPages: ModulePage[] = [
  {
    path: '/vedic-astrology-for-ios',
    name: 'Vedic Astrology',
    keyword: 'Vedic Astrology App for iOS',
    summary: 'Create and study Vedic charts on iPhone with the connected chart views available in Kundlistar Studio.',
    about: 'Vedic Astrology is the core chart system in Studio. It gives astrologers and learners an organised place to work from birth details and review the available chart analysis.',
    capabilities: ['Create Vedic birth charts', 'Review planet and house placements', 'Open supporting chart views', 'Save charts inside your Studio workspace'],
    audiences: ['Vedic astrology learners', 'Jyotish practitioners', 'Astrologers managing client charts'],
  },
  {
    path: '/kp-astrology-for-ios',
    name: 'KP Astrology',
    keyword: 'KP Astrology App for iOS',
    summary: 'Use KP chart analysis for cusps, significators and detailed chart study from your iPhone.',
    about: 'The KP module brings the Studio chart into a dedicated Krishnamurti Paddhati workflow, keeping cuspal and significator analysis connected to the saved chart.',
    capabilities: ['Study KP cusps', 'Review significators', 'Work with KP chart analysis', 'Keep analysis linked to saved charts'],
    audiences: ['KP astrologers', 'KP astrology students', 'Professionals comparing chart indications'],
  },
  {
    path: '/nadi-astrology-for-ios',
    name: 'Nadi Astrology',
    keyword: 'Nadi Astrology App for iOS',
    summary: 'Explore a dedicated Nadi chart-analysis workflow in Kundlistar Studio for iPhone.',
    about: 'Studio provides a focused Nadi module for astrologers who want Nadi analysis available beside other chart systems without moving between separate apps.',
    capabilities: ['Open dedicated Nadi analysis', 'Study saved birth charts', 'Compare with other Studio systems', 'Keep client and chart context together'],
    audiences: ['Nadi astrology practitioners', 'Nadi astrology students', 'Astrologers studying multiple systems'],
  },
  {
    path: '/bnn-astrology-for-ios',
    name: 'BNN Astrology',
    keyword: 'BNN Astrology App for iOS',
    summary: 'Study Bhrigu Nandi Nadi planet groups, strings and chart analysis on iPhone.',
    about: 'The BNN module is built for Bhrigu Nandi Nadi analysis inside the Studio workspace, including the available directional groups and combination-string views.',
    capabilities: ['Review BNN planet groups', 'Study directional groupings', 'Read available combination strings', 'Open BNN analysis from a saved chart'],
    audiences: ['BNN practitioners', 'Bhrigu Nandi Nadi students', 'Astrologers studying planet combinations'],
  },
  {
    path: '/jaimini-astrology-for-ios',
    name: 'Jaimini Astrology',
    keyword: 'Jaimini Astrology App for iOS',
    summary: 'Open a dedicated Jaimini chart-analysis module from Kundlistar Studio on iPhone.',
    about: 'Jaimini Astrology is available as its own chart module, making it easier to move from a saved chart into Jaimini-focused study while retaining the wider Studio workspace.',
    capabilities: ['Open Jaimini chart analysis', 'Use saved birth details', 'Compare chart systems', 'Organise charts by client'],
    audiences: ['Jaimini astrology learners', 'Jyotish practitioners', 'Astrologers comparing systems'],
  },
  {
    path: '/western-astrology-for-ios',
    name: 'Western Astrology',
    keyword: 'Western Astrology App for iOS',
    summary: 'View Western astrology charts alongside the Indian astrology systems in one iPhone workspace.',
    about: 'The Western chart module lets Studio users work with a Western chart view without separating it from the client, birth details and other saved analysis.',
    capabilities: ['Open Western chart views', 'Use the same saved birth details', 'Compare available chart systems', 'Keep charts organised in Studio'],
    audiences: ['Western astrology learners', 'Multi-system astrologers', 'Professionals managing saved charts'],
  },
  {
    path: '/tithi-pravesh-for-ios',
    name: 'Tithi Pravesh',
    keyword: 'Tithi Pravesh App for iOS',
    summary: 'Create and study annual Tithi Pravesh chart analysis from Kundlistar Studio on iPhone.',
    about: 'Tithi Pravesh is available as a specialised annual-chart module connected to the Studio chart workflow.',
    capabilities: ['Create Tithi Pravesh analysis', 'Study the annual chart', 'Use saved chart details', 'Keep annual analysis with the client record'],
    audiences: ['Vedic astrologers', 'Annual-chart students', 'Astrologers reviewing yearly indications'],
  },
  {
    path: '/lal-kitab-for-ios',
    name: 'Lal Kitab',
    keyword: 'Lal Kitab Astrology App for iOS',
    summary: 'Use Lal Kitab chart, Drishti and supporting analysis tools on your iPhone.',
    about: 'The Lal Kitab module provides its dedicated chart and supporting analysis inside Kundlistar Studio.',
    capabilities: ['Open the Lal Kitab chart', 'Review Lal Kitab Drishti', 'Use supporting analysis views', 'Save work with the source chart'],
    audiences: ['Lal Kitab practitioners', 'Astrology students', 'Professionals comparing chart methods'],
  },
  {
    path: '/panch-pakshi-for-ios',
    name: 'Panch Pakshi',
    keyword: 'Panch Pakshi App for iOS',
    summary: 'Check Panch Pakshi timing and activity guidance in Kundlistar Studio for iPhone.',
    about: 'Panch Pakshi gives users a dedicated view for the traditional five-bird timing system and its activity guidance.',
    capabilities: ['View Panch Pakshi timing', 'Review activity guidance', 'Use location and time context', 'Keep timing tools in one app'],
    audiences: ['Panch Pakshi practitioners', 'Traditional timing students', 'Astrologers using daily timing tools'],
  },
  {
    path: '/transit-astrology-for-ios',
    name: 'Transit Analysis',
    keyword: 'Transit Astrology App for iOS',
    summary: 'Study current and manually selected planetary transits from your iPhone.',
    about: 'Transit Analysis keeps planetary movement study connected to the saved Studio chart, with tools for current and manually selected transit context.',
    capabilities: ['Review current transits', 'Select manual transit context', 'Study transits with a saved chart', 'Move between transit and chart modules'],
    audiences: ['Transit astrology learners', 'Practising astrologers', 'Professionals reviewing chart timing'],
  },
  {
    path: '/astro-vastu-for-ios',
    name: 'Astro Vastu',
    keyword: 'Astro Vastu App for iOS',
    summary: 'Use the Astro Vastu tools available inside Kundlistar Studio on iPhone.',
    about: 'Astro Vastu is included as a specialised Studio module for users who want astrological Vastu tools alongside their chart workspace.',
    capabilities: ['Open Astro Vastu tools', 'Work from saved chart details', 'Keep analysis organised by client', 'Use the module with other Studio systems'],
    audiences: ['Astro Vastu practitioners', 'Vastu students', 'Astrologers offering multiple services'],
  },
  {
    path: '/horary-chart-calculator-for-ios',
    name: 'Horary',
    keyword: 'Horary Chart Calculator for iOS',
    summary: 'Create and analyse question-based Horary charts with Kundlistar Studio on iPhone.',
    about: 'The Horary module is designed for question-based chart work. Enter the relevant details, create the chart and continue analysis inside the Studio workspace.',
    capabilities: ['Create question-based Horary charts', 'Analyse the generated chart', 'Save chart work in Studio', 'Open related astrology tools'],
    audiences: ['Horary astrologers', 'Prashna astrology students', 'Professionals answering time-specific questions'],
  },
  {
    path: '/vedic-numerology-for-ios',
    name: 'Vedic Numerology',
    keyword: 'Vedic Numerology App for iOS',
    summary: 'Study Vedic number patterns from names and birth details on iPhone.',
    about: 'Vedic Numerology gives Studio users a dedicated number-analysis workflow based on the available name and birth-detail inputs.',
    capabilities: ['Use name and birth details', 'Review Vedic number analysis', 'Keep numerology inside Studio', 'Use alongside astrology modules'],
    audiences: ['Numerology learners', 'Vedic numerologists', 'Astrologers offering numerology'],
  },
  {
    path: '/lo-shu-grid-for-ios',
    name: 'Lo Shu Grid',
    keyword: 'Lo Shu Grid App for iOS',
    summary: 'Create and study Lo Shu number grids and patterns on your iPhone.',
    about: 'The Lo Shu Grid module creates a focused number-grid view so users can study patterns without leaving Kundlistar Studio.',
    capabilities: ['Create a Lo Shu Grid', 'Review number patterns', 'Use birth-detail inputs', 'Keep numerology and astrology together'],
    audiences: ['Lo Shu learners', 'Numerology practitioners', 'Users studying birth-date patterns'],
  },
  {
    path: '/panchang-for-ios',
    name: 'Daily Panchang',
    keyword: 'Daily Panchang App for iOS',
    summary: 'Check daily Panchang details for your selected location from iPhone.',
    about: 'Daily Panchang brings the available day and location-based Panchang details into the same Studio app as your chart work.',
    capabilities: ['View daily Panchang details', 'Use a selected location', 'Check date-based information', 'Keep daily tools with your workspace'],
    audiences: ['Daily Panchang users', 'Astrology students', 'Practitioners checking daily context'],
  },
  {
    path: '/ruling-planets-for-ios',
    name: 'Ruling Planets',
    keyword: 'Ruling Planets Calculator for iOS',
    summary: 'Calculate and review Ruling Planets in a dedicated Kundlistar Studio view.',
    about: 'The Ruling Planets tool gives Studio users a focused calculator and results view for their astrology workflow.',
    capabilities: ['Calculate Ruling Planets', 'Review the dedicated result view', 'Use date, time and place context', 'Continue work in Studio'],
    audiences: ['KP astrologers', 'Ruling Planets students', 'Practitioners using timing analysis'],
  },
  {
    path: '/astrology-app-for-ios',
    name: 'Astrology App',
    keyword: 'Astrology App for iOS',
    summary: 'Use Vedic, KP, Nadi, BNN, Jaimini, Western Astrology, Numerology and timing tools in one iOS workspace.',
    about: 'Kundlistar Studio brings its verified astrology systems together in one iPhone app, with saved charts and client-management tools available after the astrology workflow.',
    capabilities: ['Explore multiple astrology systems', 'Create and save charts', 'Use Numerology and timing tools', 'Organise chart work by client'],
    audiences: ['Astrology learners', 'Practising astrologers', 'iPhone users studying multiple systems'],
  },
  {
    path: '/astrology-app-for-iphone',
    name: 'iPhone Astrology App',
    keyword: 'Astrology App for iPhone',
    summary: 'Carry Kundlistar Studio chart systems, Numerology, timing tools and saved client work on your iPhone.',
    about: 'The iPhone app provides one organised home for Studio astrology modules and the client work connected to them.',
    capabilities: ['Open astrology modules on iPhone', 'Save and revisit charts', 'Use daily and specialised tools', 'Manage clients and consultations'],
    audiences: ['iPhone astrology users', 'Astrology students', 'Professional astrologers'],
  },
  {
    path: '/astrology-app-for-ipad',
    name: 'iPad Astrology App',
    keyword: 'Astrology App for iPad',
    summary: 'Use Kundlistar Studio astrology systems, Numerology, timing tools and client workspace on iPad.',
    about: 'The iPad experience gives Studio users more screen space for chart study while keeping saved charts and client work in the same iOS app.',
    capabilities: ['Open astrology modules on iPad', 'Study charts on a larger screen', 'Save and revisit chart work', 'Manage clients and consultations'],
    audiences: ['iPad astrology users', 'Astrology students', 'Professional astrologers'],
  },
  {
    path: '/professional-astrology-app-for-ios',
    name: 'Professional Astrology',
    keyword: 'Professional Astrology App for iOS',
    summary: 'Combine multi-system chart analysis with client CMS, planner, notes and saved-chart workflows on iPhone.',
    about: 'Kundlistar Studio supports professional astrology work by connecting its available astrology systems to an organised client and practice workspace.',
    capabilities: ['Work across available chart systems', 'Manage client profiles and linked charts', 'Record notes and consultation history', 'Plan follow-ups and tasks'],
    audiences: ['Professional astrologers', 'Astrology consultants', 'Practitioners managing repeat clients'],
  },
  {
    path: '/kundli-chart-calculator-for-ios',
    name: 'Kundli Chart Calculator',
    keyword: 'Kundli Chart Calculator for iOS',
    summary: 'Create Vedic birth charts from birth details and continue analysis in Kundlistar Studio on iPhone.',
    about: 'The Kundli chart workflow starts with birth details and keeps the resulting chart connected to the astrology modules and saved Studio workspace.',
    capabilities: ['Enter birth details', 'Create a Vedic birth chart', 'Review planet and house placements', 'Save the chart for further analysis'],
    audiences: ['People creating a Kundli', 'Vedic astrology students', 'Astrologers beginning a client analysis'],
  },
  {
    path: '/prashna-kundli-calculator-for-ios',
    name: 'Prashna Kundli Calculator',
    keyword: 'Prashna Kundli Calculator for iOS',
    summary: 'Create question-based Prashna or Horary charts and analyse them in Kundlistar Studio on iPhone.',
    about: 'Prashna Kundli uses the Studio Horary workflow for question-based chart creation and analysis.',
    capabilities: ['Create a question-based chart', 'Use date, time and place context', 'Analyse the Prashna chart', 'Save the chart inside Studio'],
    audiences: ['Prashna astrologers', 'Horary astrology students', 'Practitioners handling specific questions'],
  },
  {
    path: '/numerology-calculator-for-ios',
    name: 'Numerology Calculator',
    keyword: 'Numerology Calculator for iOS',
    summary: 'Use Vedic Numerology and Lo Shu Grid tools from names and birth details on iPhone.',
    about: 'Kundlistar Studio includes both Vedic Numerology and Lo Shu Grid modules for iOS users who want number analysis alongside astrology.',
    capabilities: ['Use name and birth details', 'Open Vedic Numerology', 'Create a Lo Shu Grid', 'Review available number patterns'],
    audiences: ['Numerology learners', 'Numerology practitioners', 'Astrologers offering number analysis'],
  },
  {
    path: '/astrology-cms-for-ios',
    name: 'Astrology Client Management',
    keyword: 'Astrology CMS for iOS',
    summary: 'Manage astrology clients, linked charts, relations, notes, consultations, planner items and supported PDFs on iPhone.',
    about: 'The Studio CMS supports the professional work around astrology analysis while keeping the available chart systems central to the product.',
    capabilities: ['Manage client profiles', 'Link saved charts and relations', 'Record notes and consultation history', 'Use planner, tasks and supported PDFs'],
    audiences: ['Professional astrologers', 'Astrology consultants', 'Practitioners organising client work'],
  },
];

export const iosModuleLandingPagePaths = iosModuleLandingPages.map((page) => page.path);

const trackAppStoreClick = (moduleName: string) => {
  const browserWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  browserWindow.dataLayer = browserWindow.dataLayer || [];
  browserWindow.dataLayer.push({
    event: 'app_store_click',
    app: 'kundlistar_studio',
    placement: 'ios_module_landing',
    module: moduleName,
  });
};

const IosModuleLandingPage: React.FC<{ path: string }> = ({ path }) => {
  const page = iosModuleLandingPages.find((item) => item.path === path);
  const { i18n } = useTranslation();
  const text = (english: string) => translatePublicSite(i18n.resolvedLanguage || i18n.language, english);

  if (!page) return null;

  const title = `${page.keyword} | Kundlistar Studio`;
  const description = `${page.summary} Kundlistar Studio supports iPhone and iPad. Premium is ₹199/month or ₹999/year, paid inside the iOS app.`;
  const faqs = [
    {
      question: `Is ${page.name} available in Kundlistar Studio for iPhone and iPad?`,
      answer: `Yes. ${page.name} is available in the Kundlistar Studio iOS app for iPhone and iPad.`,
    },
    {
      question: 'How much does Kundlistar Studio Premium cost?',
      answer: 'Premium is ₹199 per month or ₹999 per year. Payment is made inside the iOS app on iPhone or iPad and managed by Apple.',
    },
    {
      question: 'Does this plan include AI reports or add-ons?',
      answer: 'No. This iPhone offer does not include AI report plans or report add-ons.',
    },
  ];

  return (
    <Box sx={{ bgcolor: "var(--app-surface-neutral, #f7faf8)", color: "var(--app-ink-neutral, #10251a)", textAlign: 'left' }}>
      <SEO
        title={text(title)}
        description={text(description)}
        path={page.path}
        image="/promo-banner.png"
        keywords={`${page.keyword}, ${page.name} iPhone app, astrology app for iPhone, Kundlistar Studio`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          })}
        </script>
      </Helmet>

      <Box
        sx={{
          bgcolor: '#0f6e56',
          color: '#fff',
          py: { xs: 7, md: 10 },
          backgroundImage: 'radial-gradient(circle at 86% 12%, rgba(255,255,255,0.16), transparent 28%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip label={text('AVAILABLE ON IPHONE AND IPAD')} icon={<Apple />} sx={{ mb: 2.5, bgcolor: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 900 }} />
              <Typography component="h1" sx={{ color: '#fff', fontSize: { xs: '2.65rem', sm: '3.5rem', md: '4.35rem' }, lineHeight: 1.03, letterSpacing: '-0.04em', fontWeight: 900 }}>
                {text(page.keyword)}
              </Typography>
              <Typography sx={{ mt: 2.5, maxWidth: 760, color: 'rgba(255,255,255,0.88)', fontSize: { xs: 17, md: 20 }, lineHeight: 1.7 }}>
                {text(page.summary)}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  href={BRAND.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackAppStoreClick(page.name)}
                  variant="contained"
                  size="large"
                  startIcon={<Apple />}
                  sx={{ bgcolor: '#000', color: '#fff', fontWeight: 900, '&:hover': { bgcolor: '#252525' } }}
                >
                  {text('Install App')}
                </Button>
                <Button component={RouterLink} to="/install-iphone" variant="outlined" size="large" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.72)', fontWeight: 800 }}>
                  {text('View iPhone Plans')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ borderRadius: 4, boxShadow: '0 26px 70px rgba(0,0,0,0.22)' }}>
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Typography variant="overline" sx={{ color: "var(--app-ink-orange, #9b7421)", fontWeight: 900, letterSpacing: '0.12em' }}>{text('STUDIO PREMIUM')}</Typography>
                  <Typography variant="h3" sx={{ mt: 1, fontWeight: 900, color: "var(--app-ink-green, #143d28)" }}>₹999 <Typography component="span" sx={{ color: "var(--app-ink-neutral, #607066)" }}>/ {text('year')}</Typography></Typography>
                  <Typography sx={{ mt: 0.75, color: "var(--app-ink-neutral, #607066)", fontWeight: 700 }}>{text('or ₹199/month')}</Typography>
                  <Stack spacing={1.25} sx={{ mt: 3 }}>
                    {[page.name, 'All available Studio modules', 'Client CMS and saved charts'].map((item) => (
                      <Stack key={item} direction="row" spacing={1.25} alignItems="center">
                        <CheckCircle color="primary" />
                        <Typography sx={{ fontWeight: 800 }}>{text(item)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', mt: 3, color: "var(--app-ink-neutral, #607066)" }}>{text('Paid inside the iOS app. Apple manages billing.')}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="overline" sx={{ color: "var(--app-ink-orange, #9b7421)", fontWeight: 900, letterSpacing: '0.12em' }}>{text(`ABOUT ${page.name.toUpperCase()}`)}</Typography>
            <Typography component="h2" variant="h3" sx={{ mt: 1, fontWeight: 900 }}>{text(`${page.name} in your Studio workspace`)}</Typography>
            <Typography sx={{ mt: 2, color: "var(--app-ink-neutral, #607066)", fontSize: 18, lineHeight: 1.8 }}>{text(page.about)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography component="h2" variant="h4" sx={{ fontWeight: 900 }}>{text('What you can do')}</Typography>
            <Stack spacing={1.5} sx={{ mt: 2.5 }}>
              {page.capabilities.map((capability) => (
                <Stack key={capability} direction="row" spacing={1.25} alignItems="flex-start">
                  <CheckCircle color="primary" sx={{ mt: 0.25 }} />
                  <Typography sx={{ color: "var(--app-ink-neutral, #435248)", lineHeight: 1.65, fontWeight: 700 }}>{text(capability)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: "var(--app-surface-neutral, #fff)", py: { xs: 7, md: 9 }, borderBlock: '1px solid #e1e9e3' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, borderColor: "var(--app-border, #dce7df)" }}>
                <CardContent sx={{ p: 4 }}>
                  <Devices color="primary" sx={{ fontSize: 34 }} />
                  <Typography component="h2" variant="h4" sx={{ mt: 1.5, fontWeight: 900 }}>{text('Built for iPhone and iPad')}</Typography>
                  <Typography sx={{ mt: 1.5, color: "var(--app-ink-neutral, #607066)", lineHeight: 1.75 }}>{text(`Open ${page.name}, saved charts and the rest of your Studio tools from one iOS app.`)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, borderColor: "var(--app-border, #dce7df)" }}>
                <CardContent sx={{ p: 4 }}>
                  <PeopleAlt color="primary" sx={{ fontSize: 34 }} />
                  <Typography component="h2" variant="h4" sx={{ mt: 1.5, fontWeight: 900 }}>{text('CMS after the astrology')}</Typography>
                  <Typography sx={{ mt: 1.5, color: "var(--app-ink-neutral, #607066)", lineHeight: 1.75 }}>{text('Organise client profiles, linked charts, relations, notes, consultations, planner items and supported saved-chart PDFs.')}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Typography variant="overline" sx={{ color: "var(--app-ink-orange, #9b7421)", fontWeight: 900, letterSpacing: '0.12em' }}>{text('EXPLORE EVERY SYSTEM')}</Typography>
        <Typography component="h2" variant="h3" sx={{ mt: 1, fontWeight: 900 }}>{text('More Kundlistar Studio modules for iOS')}</Typography>
        <Grid container spacing={1.5} sx={{ mt: 3 }}>
          {iosModuleLandingPages.filter((item) => item.path !== page.path).map((item) => (
            <Grid key={item.path} size={{ xs: 12, sm: 6, md: 4 }}>
              <Button component={RouterLink} to={item.path} endIcon={<ArrowForward />} variant="outlined" fullWidth sx={{ justifyContent: 'space-between', py: 1.5, px: 2, fontWeight: 800, textTransform: 'none' }}>
                {text(item.name)}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: '#0d3b27', color: '#fff', py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md">
          <Typography component="h2" variant="h3" sx={{ color: '#fff', fontWeight: 900 }}>{text('Frequently asked questions')}</Typography>
          <Stack spacing={3.5} sx={{ mt: 4 }}>
            {faqs.map((faq) => (
              <Box key={faq.question}>
                <Typography component="h3" variant="h6" sx={{ color: '#fff', fontWeight: 900 }}>{text(faq.question)}</Typography>
                <Typography sx={{ mt: 0.75, color: 'rgba(255,255,255,0.78)', lineHeight: 1.75 }}>{text(faq.answer)}</Typography>
              </Box>
            ))}
          </Stack>
          <Button
            href={BRAND.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAppStoreClick(page.name)}
            variant="contained"
            size="large"
            startIcon={<Apple />}
            sx={{ mt: 4.5, bgcolor: '#000', color: '#fff', fontWeight: 900, '&:hover': { bgcolor: '#252525' } }}
          >
            {text('Install App')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default IosModuleLandingPage;
